import { getIn } from 'formik';
import { ChangeEvent, ChangeEventHandler, FocusEventHandler, KeyboardEventHandler } from 'react';
import { isEmpty } from '../../utils/getValidator';
import ErrorMessage from '../atoms/ErrorMessage';
import InputWithSuggestions from '../atoms/InputWithSuggestions';
import { TextFieldProps } from './TextField';

type TextFieldWithSuggestionsProps<T> = TextFieldProps & {
  onSelect: (value: T) => void;
  suggestions: { label: string; value: T }[];
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
};

const TextFieldWithSuggestions = <T,>({
  field,
  form: { touched, errors, setFieldValue },
  meta,
  label,
  formatOnBlur,
  ...props
}: TextFieldWithSuggestionsProps<T>) => {
  const isTouched = getIn(touched, field.name);
  const error = isTouched && getIn(errors, field.name);
  const isDropdownError = ['noResults', 'default'].includes(error);
  const hasSuccess =
    isTouched && !error && !isEmpty(typeof field.value === 'string' ? field.value.trim() : field.value);
  const { 'data-test-id': dataTestId } = props;

  const formatField: KeyboardEventHandler<HTMLInputElement> & FocusEventHandler<HTMLInputElement> = e => {
    const trimmedField = (e.currentTarget ?? e.target).value.trim();
    setFieldValue(
      field.name,
      formatOnBlur ? trimmedField.replace(new RegExp(formatOnBlur.input), formatOnBlur.output) : trimmedField
    );
  };

  const onBlur: FocusEventHandler<HTMLInputElement> = e => {
    props.onBlur?.(e);
    field.onBlur(e);
    formatField(e);
  };

  const onChange: ChangeEventHandler<HTMLInputElement> = e => {
    props.onChange(e);
    field.onChange(e);
  };

  return (
    <>
      <InputWithSuggestions
        placeholder={label}
        hasError={!isDropdownError && error}
        dropdownError={isDropdownError ? error : undefined}
        hasSuccess={hasSuccess}
        clearField={() => setFieldValue(field.name, '')}
        {...field}
        {...props}
        onBlur={onBlur}
        onChange={onChange}
      />
      <ErrorMessage label={label} error={!isDropdownError && error} dataTestId={dataTestId} />
    </>
  );
};

export default TextFieldWithSuggestions;
